import { Injectable, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { TsSnackbarService } from 'tsui';
import { BehaviorSubject, Observable, EMPTY, OperatorFunction } from 'rxjs';
import { catchError, debounceTime, filter, switchMap, tap } from 'rxjs/operators';
import { AssetManagementState } from '../state/asset-management.state';
import { AssetManagementActions } from '../state/asset-management.actions';
import { AssetManagementMessages } from '../utils/asset-management-messages.utils';
import { AssetManagementLabels } from '../enums/asset-management-reports.enum';
import { FinancialStatementTypes, ReportPeriod } from '../models';
import { TsRxjsOperators } from '@shared/Utils/custom-rxjs-operators.utils';
import { SelectorFunc } from '@ngxs/store/src/selectors';
import { AssetManagementRoutingService } from './asset-management-routing.service';
import { ReportPeriodUtils } from '../utils/report-period.utils';

@Injectable({ providedIn: 'root' })
export class AssetManagementLoaderService {
  private readonly store = inject(Store);
  private readonly snackbar = inject(TsSnackbarService);
  private readonly assetManagementRoutingService = inject(AssetManagementRoutingService);

  loadFinancialStatement(
    type: FinancialStatementTypes,
    loading: BehaviorSubject<boolean>,
    suppressSuccessMessage = false,
  ) {
    const isBalanceSheet = type === 'balance-sheet';
    const label = isBalanceSheet ? AssetManagementLabels.BalanceSheet : AssetManagementLabels.IncomeStatement;
    const selector: SelectorFunc<any> = isBalanceSheet
      ? AssetManagementState.balanceSheet
      : AssetManagementState.incomeStatement;
    const periodSelector = isBalanceSheet
      ? AssetManagementState.balanceSheetPeriod
      : AssetManagementState.incomeStatementPeriod;
    const action = isBalanceSheet
      ? AssetManagementActions.LoadBalanceSheet
      : AssetManagementActions.LoadIncomeStatement;

    return this.processReportOperator(selector, periodSelector, action, label, loading, suppressSuccessMessage);
  }

  loadBudget(loading: BehaviorSubject<boolean>, suppressSuccessMessage = false) {
    const label = 'Budget';
    const selector: SelectorFunc<any> = AssetManagementState.assetBudget;
    const periodSelector = AssetManagementState.assetBudgetReportPeriod;
    const action = AssetManagementActions.LoadAssetBudget;

    return this.processReportOperator(selector, periodSelector, action, label, loading, suppressSuccessMessage);
  }

  loadTrailingIncomeStatement(loading: BehaviorSubject<boolean>, suppressSuccessMessage = false) {
    const label = 'Trailing Income Statement';
    const selector: SelectorFunc<any> = AssetManagementState.trailingIncomeStatement;
    const periodSelector = AssetManagementState.trailingIncomeStatementPeriod;
    const action = AssetManagementActions.LoadTrailingIncomeStatement;

    return this.processReportOperator(selector, periodSelector, action, label, loading, suppressSuccessMessage);
  }

  private processReportOperator(
    featureSelector: any,
    periodSelector: SelectorFunc<ReportPeriod>,
    action: new (report: ReportPeriod) => any,
    label: string,
    loading: BehaviorSubject<boolean>,
    suppressSuccessMessage: boolean,
  ): OperatorFunction<ReportPeriod, any> {
    return (source: Observable<ReportPeriod>) =>
      source.pipe(
        filter((reportPeriod: ReportPeriod) => {
          const current = this.store.selectSnapshot(featureSelector);
          const currentPeriod = this.store.selectSnapshot(periodSelector);
          if (current == null || ReportPeriodUtils.isDiffPeriod(currentPeriod, reportPeriod)) return true;
          this.assetManagementRoutingService.updateRouteQueryParams({ ...currentPeriod });
          return false;
        }),
        debounceTime(500),
        tap(() => loading.next(true)),
        switchMap((reportPeriod: ReportPeriod) =>
          this.store
            .dispatch(new action(reportPeriod))
            .pipe(tap(() => this.assetManagementRoutingService.updateRouteQueryParams({ ...reportPeriod }))),
        ),
        TsRxjsOperators.successAndErrorSnackbar(
          this.snackbar,
          AssetManagementMessages.Success.LOAD(label),
          AssetManagementMessages.Error.LOAD(label),
          suppressSuccessMessage,
        ),
        catchError((err) => {
          loading.next(false);
          return EMPTY;
        }),
        tap(() => loading.next(false)),
      );
  }
}
